import React from 'react';
import { Box, chakra, Text } from '@chakra-ui/react';
import { useDrop } from 'react-dnd';
import { DragItem, ItemTypes } from '../dnd/types';
import DragBox from '../dnd/DragBox';
import { colors } from '../../../../../../App/ChakraTheme';

interface SlotProps {
  id: string;
  value?: string;
  showCorrect?: boolean;
  isViewOnly?: boolean;
  showResults?: boolean;
  answer?: string;
  updateValue: (toKey: string, newValue: string, fromKey: string) => void;
  slot?: string;
  onKeyboardDrop?: () => void;
  hasKeyboardSelection?: boolean;
  onRemove?: () => void;
}

const Slot: React.FC<SlotProps> = ({
  id,
  value,
  updateValue,
  slot,
  showCorrect,
  isViewOnly,
  showResults,
  answer,
  onKeyboardDrop,
  hasKeyboardSelection,
  onRemove,
}) => {
  const isDropDisabled = showResults || isViewOnly;
  const [{ highlight }, dropRef] = useDrop(
    () => ({
      accept: ItemTypes.DragBox,
      canDrop: () => !isDropDisabled,
      drop: (item: DragItem, monitor) => {
        if (isDropDisabled) return;
        updateValue(id, item.text, item.id);
      },
      collect: (monitor) => ({
        highlight: monitor.isOver(),
      }),
    }),
    [id, updateValue, isDropDisabled],
  );

  const displayValue =
    isViewOnly && showCorrect && answer ? answer : value;

  const isKeyboardTarget = hasKeyboardSelection && !isDropDisabled;

  return (
    <Box
      minW={displayValue ? undefined : '128px'}
      minH={displayValue ? undefined : '32px'}
      w="fit-content"
      border={isKeyboardTarget && !displayValue ? '2px dashed' : '1px'}
      borderRadius="8px"
      borderColor={isKeyboardTarget ? 'blue.400' : 'gray.500'}
      color="blue.500"
      background={
        highlight ? colors.applyOpacity(colors.darkblue, 0.35) : 'white'
      }
      display="inline-flex"
      alignSelf="center"
      position="relative"
      tabIndex={isKeyboardTarget ? 0 : undefined}
      role={isKeyboardTarget ? 'button' : undefined}
      aria-label={isKeyboardTarget ? 'Place selected answer here' : undefined}
      _focusVisible={{ outline: '3px solid var(--stemi-focus-color)', outlineOffset: '3px' }}
      onKeyDown={isKeyboardTarget && onKeyboardDrop ? (e: React.KeyboardEvent) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onKeyboardDrop();
        }
      } : undefined}
      ref={dropRef}
    >
      {!displayValue && showCorrect && (
        <Text m={'0 !important'} p={'0 !important'}>
          {answer}
        </Text>
      )}
      {displayValue ? (
        <>
          <DragBox
            id={slot}
            background={
              highlight ? colors.applyOpacity(colors.darkblue, 0.35) : 'white'
            }
            text={displayValue}
            noDrag={showResults || isViewOnly}
          />
          {!isDropDisabled && onRemove && (
            <Box
              as="button"
              aria-label="Remove answer"
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); onRemove(); }}
              position="absolute"
              top="-8px"
              right="-8px"
              w="18px"
              h="18px"
              borderRadius="full"
              background="gray.400"
              color="white"
              fontSize="11px"
              fontWeight="bold"
              lineHeight="18px"
              textAlign="center"
              display="flex"
              alignItems="center"
              justifyContent="center"
              _hover={{ background: 'gray.600' }}
              _focusVisible={{ outline: '2px solid var(--stemi-focus-color)', outlineOffset: '2px' }}
              zIndex={1}
            >
              ×
            </Box>
          )}
        </>
      ) : (
        <chakra.div px={2} py={1}>
          {' '}
        </chakra.div>
      )}
    </Box>
  );
};

export { Slot };
export default Slot;
